import { BorderBox } from "@/components/atoms/BorderBox";
import { HoverPopover } from "@/components/atoms/HoverPopover";
import { confirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { QuestionCheckResultProvider } from "@/features/survey_sync/context/questionCheckResultContext";
import { useSelectedItemsForSyncContext } from "@/features/survey_sync/context/selectedItemsForSyncContext";
import { useSurveyCheckResultContext } from "@/features/survey_sync/context/surveyCheckResultContext";
import { willChangeAffectResponses } from "@/features/survey_sync/lib/utils";
import { QuestionCheckResult } from "@/features/survey_sync/types/syncCheckResult";
import { ChevronsUpDown, ClipboardPen, Info } from "lucide-react";
import { QuestionLevelChange } from "../QuestionLevelChange";

export interface QuestionCheckResultItemProps extends QuestionCheckResult {
    templateCrmSystem: CrmSystemIds
    instanceCrmSystem: CrmSystemIds
}

export function QuestionCheckResultItem(props: QuestionCheckResultItemProps) {
    const { templateQuestion, instanceQuestion } = props
    const question = instanceQuestion
        || templateQuestion;
    const surveyCheckResultContext = useSurveyCheckResultContext();
    const hasDependentQuestions = surveyCheckResultContext.linkedQuestions?.[templateQuestion?.id || ""]?.length > 0;
    const dependencyForQuestionsWithIds = Object.entries(surveyCheckResultContext.linkedQuestions || {}).filter(([_, children]) => children.includes(templateQuestion?.id || "")).map(([parentId, _]) => parentId);
    const { selectedItemsForSync, setSelectedItemsForSync } = useSelectedItemsForSyncContext();
    const willChangeAffectResponsesRes = willChangeAffectResponses(
        props.dangerousChanges || []
    );
    const isChecked = Boolean(
        selectedItemsForSync.hasOwnProperty(surveyCheckResultContext.surveyInstance.id)
        && (
            (selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToSync?.includes(templateQuestion?.id || "") ?? false)
            || (props.instanceQuestion && (selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToDelete?.includes(props.instanceQuestion?.id || "") ?? false))
        )
    );
    const handleCheckboxChange = async () => {
        if (surveyCheckResultContext.surveyInstance.id === undefined) return;
        if(!isChecked && willChangeAffectResponsesRes){
            const userDecision = await confirmDialog({
                title: "This change will affect existing responses",
                description: "Syncing this question will affect existing responses. Responses for this question will be deleted. Do you want to proceed?",
                confirmText: "Yes, proceed",
                cancelText: "No, cancel"
            }).async()
            if(!userDecision.isConfirmed){
                return;
            }
        }
        // For deletion-only questions (no template), only handle questionsToDelete
        if (!templateQuestion?.id && instanceQuestion?.id) {
            const newSelected = { ...selectedItemsForSync };
            if (isChecked) {
                // Unchecking - remove from questionsToDelete
                if (newSelected[surveyCheckResultContext.surveyInstance.id]) {
                    newSelected[surveyCheckResultContext.surveyInstance.id] = {
                        survey: newSelected[surveyCheckResultContext.surveyInstance.id]?.survey,
                        questionsToSync: newSelected[surveyCheckResultContext.surveyInstance.id]?.questionsToSync,
                        questionsToDelete: newSelected[surveyCheckResultContext.surveyInstance.id]?.questionsToDelete?.filter(id => id !== instanceQuestion.id),
                    };
                    if (newSelected[surveyCheckResultContext.surveyInstance.id].questionsToSync?.length === 0 && newSelected[surveyCheckResultContext.surveyInstance.id].questionsToDelete?.length === 0) {
                        delete newSelected[surveyCheckResultContext.surveyInstance.id];
                    }
                }
            } else {
                // Checking - add to questionsToDelete
                newSelected[surveyCheckResultContext.surveyInstance.id] = {
                    survey: newSelected[surveyCheckResultContext.surveyInstance.id]?.survey,
                    questionsToSync: newSelected[surveyCheckResultContext.surveyInstance.id]?.questionsToSync || [],
                    questionsToDelete: [
                        ...(newSelected[surveyCheckResultContext.surveyInstance.id]?.questionsToDelete || []),
                        instanceQuestion.id
                    ],
                };
            }
            setSelectedItemsForSync(newSelected);
            return;
        }

        if (!templateQuestion?.id) return;

        if (isChecked) {
            if (
                dependencyForQuestionsWithIds.length > 0
                && selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToSync
                    .filter(id => dependencyForQuestionsWithIds.includes(id)).length > 0
            ) {
                const userDecision = await confirmDialog({
                    title: "This question is a dependency for other questions",
                    description: `This question is a dependency for ${dependencyForQuestionsWithIds.length} question(s) that will also be unselected. Do you want to proceed?`,
                }).async()
                if (!userDecision.isConfirmed) {
                    return;
                }
            }
            const newSelected = { ...selectedItemsForSync };
            if (newSelected[surveyCheckResultContext.surveyInstance.id]) {
                newSelected[surveyCheckResultContext.surveyInstance.id] = {
                    survey: newSelected[surveyCheckResultContext.surveyInstance.id]?.survey,
                    questionsToSync: newSelected[surveyCheckResultContext.surveyInstance.id]?.
                        questionsToSync?.
                        filter(id => id !== templateQuestion?.id),
                    questionsToDelete: newSelected[surveyCheckResultContext.surveyInstance.id]?.
                        questionsToDelete?.
                        filter(id => id !== props.instanceQuestion?.id),
                };
                newSelected[surveyCheckResultContext.surveyInstance.id] = {
                    survey: newSelected[surveyCheckResultContext.surveyInstance.id]?.survey,
                    questionsToSync: newSelected[surveyCheckResultContext.surveyInstance.id]?.
                        questionsToSync?.
                        filter(id => !dependencyForQuestionsWithIds
                            .includes(id)
                        ),
                    questionsToDelete: newSelected[surveyCheckResultContext.surveyInstance.id]?.
                        questionsToDelete?.
                        filter(id => id !== props.instanceQuestion?.id),
                };

                if (newSelected[surveyCheckResultContext.surveyInstance.id].questionsToSync?.length === 0 && newSelected[surveyCheckResultContext.surveyInstance.id].questionsToDelete?.length === 0) {
                    delete newSelected[surveyCheckResultContext.surveyInstance.id];
                }
                setSelectedItemsForSync(newSelected);
            }
        } else {
            if (hasDependentQuestions) {
                const userDecision = await confirmDialog({
                    title: "This question has dependent questions",
                    description: `This question has ${surveyCheckResultContext.linkedQuestions?.[templateQuestion?.id || ""]?.length} dependent question(s) that will also be selected. Do you want to proceed?`,
                }).async()
                if (userDecision.isConfirmed) {
                    const questionsToAdd = surveyCheckResultContext.linkedQuestions?.[templateQuestion?.id || ""] || [];
                    const dependantQuestionsAlreadySelected = selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToSync?.filter(id => questionsToAdd.includes(id)) || [];
                    const questionsToAddFiltered = questionsToAdd.filter(id => !dependantQuestionsAlreadySelected.includes(id));
                    setSelectedItemsForSync({
                        ...selectedItemsForSync,
                        [surveyCheckResultContext.surveyInstance.id]: {
                            survey: selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.survey,
                            questionsToSync: [
                                ...(selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToSync || []),
                                templateQuestion?.id,
                                ...questionsToAddFiltered
                            ],
                            questionsToDelete: [
                                ...(selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToDelete || []),
                                instanceQuestion?.id
                            ].filter(id => id !== undefined),
                        }
                    });
                }
            } else {
                setSelectedItemsForSync({
                    ...selectedItemsForSync,
                    [surveyCheckResultContext.surveyInstance.id]: {
                        survey: selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.survey,
                        questionsToSync: [
                            ...(selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToSync || []),
                            templateQuestion?.id
                        ],
                        questionsToDelete: [
                            ...(selectedItemsForSync[surveyCheckResultContext.surveyInstance.id]?.questionsToDelete || []),
                            instanceQuestion?.id
                        ].filter(id => id !== undefined),
                    }
                });
            }
        }
    };
    return (
        <QuestionCheckResultProvider value={props}>
            <BorderBox size={"sm"} className="flex flex-col items-start bg-white" icon={false}>
                <div className="flex items-center justify-between w-full">
                    <div className="">
                        <h3 className="flex flex-wrap gap-1">
                            <span className="flex items-center gap-1"><ClipboardPen className="inline-block" size="16" /> Question:</span>
                            <span className="font-semibold">{question?.question}</span>
                        </h3>
                        <h3 className="flex flex-wrap gap-1">
                            <span className="">Description:</span>
                            <span className="">{question?.description}</span>
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <p>Sync question</p>
                        {(hasDependentQuestions || dependencyForQuestionsWithIds.length > 0) && (
                            <HoverPopover
                                onHover
                                content={
                                    <div className="flex flex-col w-full max-w-md gap-2 p-2">
                                        {hasDependentQuestions && (
                                            <>
                                                <h4 className="text-sm">This question has dependent questions</h4>
                                                <p className="text-xs">If you select this question for sync, all questions below will be selected as well:</p>
                                                <div className="flex flex-col gap-2">
                                                    {surveyCheckResultContext.linkedQuestions?.[templateQuestion?.id || ""]?.map((qId) => {
                                                        const question = surveyCheckResultContext.questionsToSync.find(q => q.templateQuestion?.id === qId);
                                                        return (
                                                            <BorderBox key={qId} size="sm" className="flex flex-col items-start p-2">
                                                                <h4 className="text-xs font-semibold">{question?.templateQuestion?.question}</h4>
                                                            </BorderBox>
                                                        )
                                                    })}
                                                </div>
                                            </>
                                        )}
                                        {dependencyForQuestionsWithIds.length > 0 && (
                                            <>
                                                <h4 className="text-sm">This question is a dependency for other questions</h4>
                                                <p className="text-xs">If you unselect this question for sync, all questions below will be unselected as well:</p>
                                                <div className="flex flex-col gap-2">
                                                    {dependencyForQuestionsWithIds.map((qId) => {
                                                        const question = surveyCheckResultContext.questionsToSync.find(q => q.templateQuestion?.id === qId);
                                                        return (
                                                            <BorderBox key={qId} size="sm" className="flex flex-col items-start p-2">
                                                                <h4 className="text-xs font-semibold">{question?.templateQuestion?.question}</h4>
                                                            </BorderBox>
                                                        )
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                }
                                popoverContentProps={{ className: "max-w-xl w-full" }}
                            >
                                <Info className="cursor-pointer" size={16} />
                            </HoverPopover>
                        )}
                        <Checkbox
                            checked={isChecked}
                            onCheckedChange={handleCheckboxChange}
                        />
                    </div>
                </div>

                {props.dangerousChanges && props.dangerousChanges.length > 0 && (
                    <Collapsible className="w-full">
                        <div className="flex flex-col w-full gap-2">
                            <BorderBox variant={"info"} className="flex justify-between w-full" size="xs">
                                <h4 className="">There are {props.dangerousChanges.length} changes to question</h4>
                                <CollapsibleTrigger asChild>
                                    <Button variant="outline" size="action"><ChevronsUpDown size={14} /></Button>
                                </CollapsibleTrigger>
                            </BorderBox>

                            <CollapsibleContent className="w-full">
                                <BorderBox className="flex flex-col items-start w-full gap-2 bg-white">
                                    {props.dangerousChanges?.map((change, index) => (
                                        <QuestionLevelChange
                                            key={index}
                                            change={change}
                                            instanceCrmSystem={props.instanceCrmSystem}
                                            templateCrmSystem={props.templateCrmSystem}
                                            instanceQuestion={props.instanceQuestion}
                                            templateQuestion={props.templateQuestion}
                                        />
                                    ))}
                                </BorderBox>
                            </CollapsibleContent>

                        </div>
                    </Collapsible>
                )}
            </BorderBox>
        </QuestionCheckResultProvider>
    )
}